// Owner-agnostic container for the F-F media/narration <MediaPanel>. Holds the
// logic that used to be copied byte-for-byte in ZoneMediaPanel + ExhibitMediaPanel
// (review #8): narration-row shaping, the unsaved-changes generate guard, the
// remove-narration branch (stored asset → clear the ref; legacy voice URL → wipe
// it so the visitor falls back to browser TTS), client-side size limits, and the
// toast wrapping. The thin owner wrappers just call their React Query hooks and
// pass id-bound `ops` — the only per-owner difference. Snake_case wire fields
// per ADR-0020.

import type { FC } from "react";
import { toast } from "sonner";

import { assetUrl } from "@shared/api/assets";
import { extractErrorMessage as errMsg } from "@shared/api/errors";
import type {
  AssetKind,
  MediaItem,
  NarrationSource,
} from "@shared/api/exhibits";
import { downloadFile } from "@shared/lib/download";
import type { AudioLanguage } from "@shared/hooks/useTextToSpeech";

import MediaPanel from "@staff/features/media/MediaPanel";

// Client-side guards mirroring the backend MAX_*_BYTES (the server enforces them
// too — this just gives a faster, friendlier error).
const MAX_BYTES: Partial<Record<AssetKind, number>> = {
  audio: 100 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  pdf: 100 * 1024 * 1024,
};

// The slice of a Zone / Exhibit InDB the container reads — both satisfy it.
export interface MediaNarrationOwner {
  media?: MediaItem[] | null;
  narrationAssetIdTc?: string | null;
  narrationAssetIdEn?: string | null;
  narrationAssetIdSc?: string | null;
  narrationSourceTc?: NarrationSource | null;
  narrationSourceEn?: NarrationSource | null;
  narrationSourceSc?: NarrationSource | null;
  voiceUrlTc?: string | null;
  voiceUrlEn?: string | null;
  voiceUrlSc?: string | null;
}

// Per-owner glue: each op is already bound to the owner id by the wrapper, so the
// container never touches zoneId/exhibitId or the entity-specific hook types.
export interface MediaPanelOps {
  generate: (language: AudioLanguage) => Promise<unknown>;
  uploadNarration: (language: AudioLanguage, file: File) => Promise<unknown>;
  setNarrationNull: (language: AudioLanguage) => Promise<unknown>;
  clearVoiceUrl: (language: AudioLanguage) => Promise<unknown>;
  saveVoiceUrl: (language: AudioLanguage, url: string) => Promise<unknown>;
  uploadMedia: (params: {
    file: File;
    kind: AssetKind;
    language: string | null;
    caption: string | null;
  }) => Promise<unknown>;
  updateMedia: (
    assetId: string,
    patch: { caption: string | null; language: string | null },
  ) => Promise<unknown>;
  removeMedia: (assetId: string) => Promise<unknown>;
}

export interface MediaPanelContainerProps {
  ownerKey: "zone" | "exhibit";
  entity: MediaNarrationOwner;
  ops: MediaPanelOps;
  generatingLang: AudioLanguage | null;
  uploadingMedia: boolean;
  disabled?: boolean;
  unsavedChanges?: boolean;
  // #2: per-language "description edited since load" flags, computed by the form
  // (current vs saved text). Drives the per-row regenerate nudge.
  descriptionChanged?: Partial<Record<AudioLanguage, boolean>>;
}

const MediaPanelContainer: FC<MediaPanelContainerProps> = ({
  ownerKey,
  entity,
  ops,
  generatingLang,
  uploadingMedia,
  disabled,
  unsavedChanges,
  descriptionChanged,
}) => {
  const narration = {
    tc: {
      assetId: entity.narrationAssetIdTc ?? null,
      voiceUrl: entity.voiceUrlTc ?? "",
      source: entity.narrationSourceTc ?? null,
      descriptionChanged: descriptionChanged?.tc ?? false,
    },
    en: {
      assetId: entity.narrationAssetIdEn ?? null,
      voiceUrl: entity.voiceUrlEn ?? "",
      source: entity.narrationSourceEn ?? null,
      descriptionChanged: descriptionChanged?.en ?? false,
    },
    sc: {
      assetId: entity.narrationAssetIdSc ?? null,
      voiceUrl: entity.voiceUrlSc ?? "",
      source: entity.narrationSourceSc ?? null,
      descriptionChanged: descriptionChanged?.sc ?? false,
    },
  };

  const tooLarge = (file: File, kind: AssetKind): boolean => {
    const limit = MAX_BYTES[kind] ?? MAX_BYTES.audio!;
    if (file.size > limit) {
      toast.error(
        `File must be less than ${Math.round(limit / 1024 / 1024)}MB`,
      );
      return true;
    }
    return false;
  };

  const handleGenerate = async (language: AudioLanguage) => {
    if (unsavedChanges) {
      toast.error(
        "Save your changes first — narration is generated from the saved description.",
      );
      return;
    }
    try {
      await ops.generate(language);
      toast.success(`Generated ${language.toUpperCase()} narration`);
    } catch (err) {
      toast.error(errMsg(err, "Failed to generate narration"));
    }
  };

  const handleUploadNarration = async (language: AudioLanguage, file: File) => {
    if (tooLarge(file, "audio")) return;
    try {
      await ops.uploadNarration(language, file);
      toast.success(`Uploaded ${language.toUpperCase()} narration`);
    } catch (err) {
      toast.error(errMsg(err, "Failed to upload narration"));
    }
  };

  const handleDownloadNarration = async (language: AudioLanguage) => {
    const assetId = narration[language].assetId;
    if (!assetId) return;
    try {
      // Named per owner + language (e.g. narration-zone-tc.mp3); the helper
      // derives the extension from the response MIME.
      await downloadFile(
        assetUrl(assetId),
        `narration-${ownerKey}-${language}`,
      );
    } catch (err) {
      toast.error(errMsg(err, "Failed to download narration"));
    }
  };

  const handleRemoveNarration = async (language: AudioLanguage) => {
    try {
      if (narration[language].assetId) {
        await ops.setNarrationNull(language);
      } else {
        // Legacy voice URL (no stored asset) — clear it via a content update so
        // the visitor falls back to browser TTS. Voice URLs are no longer edited
        // in the Translations section; this is the only place they're managed.
        await ops.clearVoiceUrl(language);
      }
      toast.success(`Removed ${language.toUpperCase()} narration`);
    } catch (err) {
      toast.error(errMsg(err, "Failed to remove narration"));
    }
  };

  const handleSaveVoiceUrl = async (language: AudioLanguage, url: string) => {
    try {
      await ops.saveVoiceUrl(language, url);
      toast.success(`Saved ${language.toUpperCase()} narration URL`);
    } catch (err) {
      toast.error(errMsg(err, "Failed to save narration URL"));
    }
  };

  const handleUploadMedia = async ({
    file,
    kind,
    language,
    caption,
  }: {
    file: File;
    kind: AssetKind;
    language: string | null;
    caption: string | null;
  }) => {
    if (tooLarge(file, kind)) return;
    try {
      await ops.uploadMedia({ file, kind, language, caption });
      toast.success("Media uploaded");
    } catch (err) {
      toast.error(errMsg(err, "Failed to upload media"));
    }
  };

  const handleUpdateMedia = async (
    assetId: string,
    patch: { caption: string | null; language: string | null },
  ) => {
    try {
      await ops.updateMedia(assetId, patch);
    } catch (err) {
      toast.error(errMsg(err, "Failed to update media"));
    }
  };

  const handleRemoveMedia = async (assetId: string) => {
    try {
      await ops.removeMedia(assetId);
      toast.success("Media removed");
    } catch (err) {
      toast.error(errMsg(err, "Failed to remove media"));
    }
  };

  return (
    <MediaPanel
      ownerKey={ownerKey}
      assetUrl={assetUrl}
      disabled={disabled}
      narration={narration}
      generatingLang={generatingLang}
      onGenerate={handleGenerate}
      onUploadNarration={handleUploadNarration}
      onDownloadNarration={handleDownloadNarration}
      onRemoveNarration={handleRemoveNarration}
      onSaveVoiceUrl={handleSaveVoiceUrl}
      media={entity.media ?? []}
      uploadingMedia={uploadingMedia}
      onUploadMedia={handleUploadMedia}
      onUpdateMedia={handleUpdateMedia}
      onRemoveMedia={handleRemoveMedia}
    />
  );
};

export default MediaPanelContainer;
